import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { fetchPlans } from '../api/authApi';
import { getFeatures, getPlanFeatures, updatePlanFeatures } from '../api/entitlementsApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { PageHeader } from '../components/PageHeader';

export function PlanFeaturesPage() {
  const { t } = useTranslation(['billing', 'common']);
  const { id } = useParams();
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.features.manage');

  const [plan, setPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [plansResponse, allFeaturesResponse, planFeaturesResponse] = await Promise.all([
        fetchPlans(),
        getFeatures({ per_page: 200, source: 'legacy', include_legacy: true }),
        getPlanFeatures(id),
      ]);

      const currentPlan = (plansResponse.data || []).find((item) => String(item.id) === String(id));
      setPlan(currentPlan || null);

      const allFeatures = allFeaturesResponse.data || [];
      setFeatures(allFeatures);

      const map = {};
      (planFeaturesResponse.data || []).forEach((item) => {
        map[item.feature_id] = {
          enabled: item.enabled,
          limit_value: item.limit_value ?? '',
        };
      });
      setAssignments(map);
    } catch {
      setError(t('billing:load_plan_features_failed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleFeature = (featureId, enabled) => {
    setAssignments((prev) => ({
      ...prev,
      [featureId]: {
        ...prev[featureId],
        enabled,
        limit_value: prev[featureId]?.limit_value ?? '',
      },
    }));
  };

  const setLimit = (featureId, limitValue) => {
    setAssignments((prev) => ({
      ...prev,
      [featureId]: {
        ...prev[featureId],
        enabled: prev[featureId]?.enabled ?? false,
        limit_value: limitValue,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');

    try {
      const payload = {
        features: features.map((feature) => ({
          feature_id: feature.id,
          enabled: Boolean(assignments[feature.id]?.enabled),
          value: { enabled: Boolean(assignments[feature.id]?.enabled) },
          limit_value: assignments[feature.id]?.limit_value
            ? Number(assignments[feature.id].limit_value)
            : null,
        })),
      };

      await updatePlanFeatures(id, payload);
      setMessage(t('billing:plan_features_saved'));
      load();
    } catch {
      setMessage(t('billing:plan_features_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const grouped = features.reduce((acc, feature) => {
    const moduleName = feature.module?.name || t('common:other');
    if (!acc[moduleName]) {
      acc[moduleName] = [];
    }
    acc[moduleName].push(feature);
    return acc;
  }, {});

  return (
    <Box>
      <PageHeader
        title={plan ? t('billing:plan_features_named', { name: plan.name }) : t('billing:plan_features_title')}
        subtitle={plan?.code}
        backTo="/plans"
      />

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      {Object.entries(grouped).map(([moduleName, moduleFeatures]) => (
        <Paper key={moduleName} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>{moduleName}</Typography>
          <Stack spacing={1}>
            {moduleFeatures.map((feature) => (
              <Stack key={feature.id} direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(assignments[feature.id]?.enabled)}
                      onChange={(e) => toggleFeature(feature.id, e.target.checked)}
                      disabled={!canManage}
                    />
                  )}
                  label={`${feature.name} (${feature.code})`}
                />
                {feature.value_type === 'integer' && (
                  <TextField
                    size="small"
                    label={t('billing:limit')}
                    type="number"
                    value={assignments[feature.id]?.limit_value ?? ''}
                    onChange={(e) => setLimit(feature.id, e.target.value)}
                    disabled={!canManage}
                    sx={{ width: 120 }}
                  />
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      ))}

      {canManage && (
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {t('billing:save_plan_features')}
        </Button>
      )}

      <Typography variant="body2" sx={{ mt: 2 }}>
        <RouterLink to="/plans">{t('billing:back_to_plans')}</RouterLink>
      </Typography>
    </Box>
  );
}
